import React from 'react';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { Grid } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { useDispatch } from 'react-redux';
import ActionIcon from '../MUI/Icons/ActionIcon';
import getFullNameByLang from '../../helpers/langHelpers';
import MainSwitch from '../MUI/MainSwitch';
import { ACTION_TYPES, DATE_FORMAT_DISPLAY } from '../../constants';

import useStyles from './makeStyles';
import styles from './PersonCard.module.scss';

const { personCard, head, buttonContainer, personBody, invalid } = styles;

const getDateString = (date) => date && format(date, DATE_FORMAT_DISPLAY);

const PersonCard = ({
  contractType,
  person,
  index,
  editHandler,
  removeHandler,
  isInvalid,
  isSigner,
  hasAddedSigner,
  setHasAddedSigner,
  personIcon,
  goPrevious,
  disabledHasAddedSigner,
  disabledCard,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const lang = i18n.language;
  const classes = useStyles();

  const columns = [
    {
      labelKey: 'personalInfo.dob',
      value: getDateString(person.birthday),
    },
    {
      labelKey: 'passportIdInfo.passportNumber',
      value: person.passport,
    },
  ];

  return (
    <div className={cn(personCard, isInvalid && invalid)}>
      <div className={head}>
        <div className={classes.headStart}>
          <div className={classes.headIcon}>
            <img className={classes.personIcon} alt="" src={personIcon} />
            {!isSigner && (
              <div className={classes.personIndexContainer}>
                <Typography color="primary" className={classes.personIndex}>
                  {index}
                </Typography>
              </div>
            )}
          </div>
          <div className={classes.headTitle}>
            <Typography className={classes.sectionTitle}>
              {getFullNameByLang(person, lang)}
            </Typography>
            {isSigner && (
              <Typography color="primary" className={classes.sectionSecondaryTitle}>
                {t('contract.signer')}
              </Typography>
            )}
          </div>
        </div>
        <div className={buttonContainer}>
          <ActionIcon
            type={ACTION_TYPES.EDIT}
            disabled={disabledCard}
            onClick={() => !disabledCard
                && (isSigner ? goPrevious() : editHandler && editHandler(person))}
          />
          {removeHandler && (
            <ActionIcon
              type={ACTION_TYPES.DELETE}
              disabled={disabledCard}
              onClick={() => !disabledCard && removeHandler(person)}
            />
          )}
        </div>
      </div>
      <div className={personBody}>
        <Grid container spacing={1} alignItems="center">
          {columns.map((col) => (
            <React.Fragment key={col.labelKey}>
              <Grid item xs={6}>
                <Typography className={classes.labelText} variant="h6" component="span">
                  {t(col.labelKey)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography align="right">{col.value}</Typography>
              </Grid>
            </React.Fragment>
          ))}

          {isSigner && (
            <>
              <Grid item xs={9}>
                <Typography className={classes.labelText} variant="h6" component="span">
                  {t(`${contractType}.sameSigner`)}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <MainSwitch
                  color="primary"
                  className={classes.hasAddedSigner}
                  checked={hasAddedSigner || false}
                  disabled={disabledHasAddedSigner}
                  onChange={(e) => dispatch(setHasAddedSigner(e.target.checked))}
                />
              </Grid>
            </>
          )}
        </Grid>
      </div>
    </div>
  );
};

PersonCard.propTypes = {
  contractType: PropTypes.string.isRequired,
  person: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    firstNameEn: PropTypes.string,
    lastNameEn: PropTypes.string,
    birthday: PropTypes.instanceOf(Date),
    gender: PropTypes.number,
    socCard: PropTypes.string,
    passport: PropTypes.string,
    passportGiven: PropTypes.instanceOf(Date),
    passportValidUntil: PropTypes.instanceOf(Date),
    passportFrom: PropTypes.string,
    citizenship: PropTypes.shape({}),
  }),
  index: PropTypes.number,
  editHandler: PropTypes.func,
  removeHandler: PropTypes.func,
  isInvalid: PropTypes.bool,
  isSigner: PropTypes.bool,
  hasAddedSigner: PropTypes.bool,
  setHasAddedSigner: PropTypes.func,
  personIcon: PropTypes.string.isRequired,
  goPrevious: PropTypes.func.isRequired,
  disabledHasAddedSigner: PropTypes.bool,
  disabledCard: PropTypes.bool,
};

PersonCard.defaultProps = {
  person: {},
  index: null,
  editHandler: null,
  removeHandler: null,
  isInvalid: false,
  isSigner: false,
  hasAddedSigner: false,
  disabledHasAddedSigner: false,
  setHasAddedSigner: null,
  disabledCard: false,
};

export default PersonCard;
